import { Slab } from '../../render/parts'
import { BookStack } from '../parts/props'
import { BinderRow, Carcass, DoorFronts, Shelves } from '../parts/storage'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 0.9
const DEPTH = 0.45
const HEIGHT = 1.85
const WALL = 0.02
const PLINTH = 0.06

/** Front plane of the carcass — doors and pulls stack forward from here. */
const FRONT = DEPTH / 2
/** Clear opening between the bottom and top boards of the carcass. */
const OPEN_BOTTOM = PLINTH + WALL
const OPEN_TOP = HEIGHT - WALL
/** Height where the unit splits into a closed lower half and an upper half. */
const SPLIT = 0.96
/** The single shelf that divides the upper half into two binder bays. */
const UPPER_SHELF = 1.4
/** Door runs are inset from the carcass sides so no two faces end up coplanar. */
const DOOR_WIDTH = WIDTH - 0.012

const LOWER_TOP = SPLIT - 0.015
const LOWER_HEIGHT = LOWER_TOP - OPEN_BOTTOM - 0.01
const LOWER_Y = OPEN_BOTTOM + 0.01 + LOWER_HEIGHT / 2

const UPPER_BOTTOM = SPLIT + 0.015
const UPPER_HEIGHT = OPEN_TOP - 0.005 - UPPER_BOTTOM
const UPPER_Y = UPPER_BOTTOM + UPPER_HEIGHT / 2

const FULL_HEIGHT = OPEN_TOP - OPEN_BOTTOM - 0.02
const FULL_Y = OPEN_BOTTOM + 0.01 + FULL_HEIGHT / 2

interface PullProps {
  x: number
  y: number
  z: number
  length: number
  accent?: string
}

/**
 * Slim vertical finger pull — the only accent note on an otherwise white front.
 * Hinged leaves are pulled from the join, so the pair reads as one gesture.
 */
function Pull({ x, y, z, length, accent }: PullProps) {
  return (
    <Slab
      tone="accent"
      color={accent}
      size={[0.024, length, 0.014]}
      radius={0.006}
      rotation={[Math.PI / 2, 0, 0]}
      position={[x, y, z]}
    />
  )
}

/** Leaf centres for a two-door run, matching the layout DoorFronts uses. */
function leafCentre(index: number) {
  const gap = 0.006
  const leaf = (DOOR_WIDTH - gap * 3) / 2
  return -DOOR_WIDTH / 2 + gap + leaf / 2 + index * (leaf + gap)
}

/** Pull x for a two-door run: both handles sit either side of the centre join. */
function pullX(index: number) {
  const gap = 0.006
  const leaf = (DOOR_WIDTH - gap * 3) / 2
  return leafCentre(index) + (index === 0 ? 1 : -1) * (leaf / 2 - 0.045)
}

/** Binders and books on the two open (or glazed) bays. */
function Contents({ accent }: { accent?: string }) {
  return (
    <group>
      <group position={[-0.19, 0, -0.02]}>
        <BinderRow width={0.4} y={SPLIT + 0.007} height={0.28} accent={accent} seed={2} />
      </group>
      <group position={[0.24, 0, -0.02]}>
        <BookStack base={SPLIT + 0.007} width={0.2} depth={0.17} accent={accent} seed={5} />
      </group>
      <group position={[0.16, 0, -0.02]}>
        <BookStack base={UPPER_SHELF + 0.007} width={0.22} depth={0.17} accent={accent} seed={1} />
      </group>
    </group>
  )
}

/**
 * Full-height storage cupboard, 0.9 x 0.45 x 1.85. Origin at the centre of the
 * footprint, y = 0 on the floor, doors facing +Z. Too tall to stack onto, so it
 * declares no surface height.
 */
function CabinetTall({ accent, variant = 'closed' }: AssetContext) {
  const closed = variant === 'closed'
  const glazed = variant === 'glazed'
  const doorZ = FRONT + 0.008
  const pullZ = doorZ + 0.013

  return (
    <group>
      <Carcass width={WIDTH} depth={DEPTH} height={HEIGHT} plinth={PLINTH} wall={WALL} />

      {closed ? (
        <group>
          <DoorFronts
            width={DOOR_WIDTH}
            height={FULL_HEIGHT}
            y={FULL_Y}
            depth={DEPTH}
            count={2}
            handle="none"
          />
          {[0, 1].map((index) => (
            <Pull key={index} x={pullX(index)} y={1.05} z={pullZ} length={0.5} accent={accent} />
          ))}
        </group>
      ) : (
        <group>
          {/* Shelf at SPLIT closes the lower half; the one above splits the bays. */}
          <Shelves
            width={WIDTH}
            depth={DEPTH - 0.012}
            levels={[SPLIT, UPPER_SHELF]}
            inset={0.016}
          />
          <Contents accent={accent} />

          <DoorFronts
            width={DOOR_WIDTH}
            height={LOWER_HEIGHT}
            y={LOWER_Y}
            depth={DEPTH}
            count={2}
            handle="none"
          />
          {[0, 1].map((index) => (
            <Pull
              key={index}
              x={pullX(index)}
              y={LOWER_TOP - 0.17}
              z={pullZ}
              length={0.26}
              accent={accent}
            />
          ))}

          {glazed ? (
            <group>
              <DoorFronts
                width={DOOR_WIDTH}
                height={UPPER_HEIGHT}
                y={UPPER_Y}
                depth={DEPTH}
                count={2}
                handle="none"
                glass
              />
              {[0, 1].map((index) => (
                <Pull
                  key={index}
                  x={pullX(index)}
                  y={UPPER_BOTTOM + 0.17}
                  z={pullZ}
                  length={0.26}
                  accent={accent}
                />
              ))}
            </group>
          ) : null}
        </group>
      )}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'cabinet-tall',
  name: 'Armario alto',
  category: 'storage',
  footprint: [WIDTH, DEPTH],
  height: HEIGHT,
  accentable: true,
  variants: [
    { id: 'closed', name: 'Cerrado' },
    { id: 'mixed', name: 'Mixto' },
    { id: 'glazed', name: 'Vitrina' },
  ],
  keywords: [
    'armario',
    'alacena',
    'archivador',
    'vitrina',
    'almacenaje',
    'tall cabinet',
    'cupboard',
    'wardrobe',
    'storage',
  ],
  Component: CabinetTall,
}
